import { MIN_ROLLOUT_WEIGHT } from '@gonfalon/automated-rollouts/guarded-rollouts/constants';
import { maximumAllowedMonitoredRolloutDurationMs } from '@gonfalon/dogfood-flags/dogfood-flags';
import { getMsFromDuration } from '@gonfalon/format/getDisplayDurationFromMs';
import { pluralize } from '@gonfalon/format/pluralize';
// oxlint-disable-next-line no-restricted-imports -- TODO(zod-4): migrate to 'zod/v4'
import { z } from 'zod';

import type { RolloutType } from '../types';
import { convertMsToDisplayDuration } from '../utils/convertMsToDisplayDuration';
import type { durationUnits } from '../utils/durationConstants';
import { getDurationQuantityLimits } from '../utils/getDurationQuantityLimits';

function createRolloutWeightSchema(rolloutType: RolloutType) {
  const schema = z
    // eslint-disable-next-line @typescript-eslint/naming-convention
    .number({ required_error: 'Set rollout percentage' })
    .int('Set rollout percentage to be an integer')
    .min(MIN_ROLLOUT_WEIGHT, 'Increase rollout percentage to at least 0.001%');

  switch (rolloutType) {
    case 'guarded':
      return schema.lte(50000, 'Rollout percentage cannot exceed 50% per step');
    case 'progressive':
      return schema.lt(100000, 'Decrease rollout percentage to less than 100%');
    default:
      return schema.lt(100000, 'Decrease rollout percentage to less than 100%');
  }
}

function createDurationSchema<T extends (typeof durationUnits)[number]>(unit: T) {
  const limits = getDurationQuantityLimits()[unit];
  const pluralUnit = unit === 'day' ? 'days' : unit === 'hour' ? 'hours' : 'minutes';

  let quantitySchema = z
    .number({ invalid_type_error: 'Enter a duration' })
    .min(limits.min, `Increase duration to at least ${limits.min} ${pluralize(limits.min, unit, pluralUnit)}`);

  if (limits.max !== undefined) {
    quantitySchema = quantitySchema.max(
      limits.max,
      `Decrease duration to no more than ${limits.max} ${pluralize(limits.max, unit, pluralUnit)}`,
    );
  }

  return z.object({
    quantity: quantitySchema,
    unit: z.literal(unit),
  });
}

function createProgressiveRolloutConfigurationStepSchema(rolloutType: RolloutType) {
  return z.object({
    key: z.string().min(1, 'step key is required'),
    rolloutWeight: createRolloutWeightSchema(rolloutType),
    duration: z.union([createDurationSchema('day'), createDurationSchema('hour'), createDurationSchema('minute')]),
  });
}

export const progressiveRolloutConfigurationStepSchema = createProgressiveRolloutConfigurationStepSchema('progressive');

export type ProgressiveRolloutConfigurationStep = z.infer<typeof progressiveRolloutConfigurationStepSchema>;

function createProgressiveRolloutConfigurationStepsSchema(rolloutType: RolloutType) {
  return z.array(createProgressiveRolloutConfigurationStepSchema(rolloutType)).superRefine((steps, ctx) => {
    // Validate that rollout weights are increasing
    toNumberPairs(steps.map((step) => step.rolloutWeight)).forEach(([a, b], aIndex) => {
      if (b <= a) {
        ctx.addIssue({
          code: 'custom',
          message: 'Increase rollout percentage from previous step',
          path: [aIndex + 1, 'rolloutWeight'],
        });
      }
    });

    // Validate total duration doesn't exceed the maximum allowed monitored rollout duration
    const maxTotalDurationMs = maximumAllowedMonitoredRolloutDurationMs();
    if (maxTotalDurationMs > 0) {
      const totalDurationMs = steps.reduce((total, step) => total + getMsFromDuration(step.duration), 0);

      if (totalDurationMs > maxTotalDurationMs) {
        const maxTotalDurationDisplay = convertMsToDisplayDuration(maxTotalDurationMs);
        const totalDurationDisplay = convertMsToDisplayDuration(totalDurationMs);
        ctx.addIssue({
          code: 'custom',
          message: `Total rollout duration (${totalDurationDisplay}) exceeds the maximum allowed duration (${maxTotalDurationDisplay})`,
          path: [],
        });
      }
    }
  });
}

export const progressiveRolloutConfigurationStepsSchema =
  createProgressiveRolloutConfigurationStepsSchema('progressive');
export const guardedRolloutConfigurationStepsSchema = createProgressiveRolloutConfigurationStepsSchema('guarded');

export type ProgressiveRolloutConfigurationSteps = z.infer<typeof progressiveRolloutConfigurationStepsSchema>;

type NumberPair = [a: number, b: number];
const toNumberPairs = (arr: number[]) => arr.slice(1).map((e, i): NumberPair => [arr[i], e]);
